import { useRef, useEffect, useState } from 'react'
import { motion, useScroll, useTransform, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, ArrowUpRight, Play, MapPin, Users, Calendar, Star, Clock, Heart, Flower2, TreePine, Egg, Coffee, UtensilsCrossed, Sparkles, Briefcase, PartyPopper, ChevronLeft, ChevronRight, Quote, Award } from 'lucide-react'
import { getEvents, transformEventForDisplay, type Event } from '../services/eventsService'
import { brunchService, Brunch } from '../services/brunchService'
import { getEventImageUrl, EVENT_IMAGE_FALLBACK } from '../config/api'

// Icon Mapping
const iconMap: Record<string, any> = {
  Heart,
  Flower2,
  TreePine,
  Egg,
  Coffee,
  UtensilsCrossed,
  Sparkles
}

// Daten für die Event-Kategorien
const eventCategories = [
  {
    id: 'hochzeiten',
    title: 'Hochzeiten',
    subtitle: 'Hier beginnt Ihre gemeinsame Zukunft',
    description: 'Historisches Ambiente für den wichtigsten Tag Ihres Lebens.',
    image: '/home_pics/hochzeit.jpeg',
    link: '/hochzeiten',
    capacity: 'Bis 170 Gäste'
  },
  {
    id: 'corporate',
    title: 'Firmenfeiern',
    subtitle: 'Wo Geschäft auf Eleganz trifft',
    description: 'Professionelle Events für Teams von 20 bis 2000+ Personen.',
    image: '/assets/home_pics/HK_184.jpg',
    link: '/unternehmensfeiern',
    capacity: 'Bis 2000+ Gäste'
  },
  {
    id: 'private',
    title: 'Private Feiern',
    subtitle: 'Feiern Sie die schönen Momente',
    description: 'Geburtstage, Jubiläen und besondere Anlässe.',
    image: '/assets/home_pics/DS2.JPG',
    link: '/private-feiern',
    capacity: 'Flexibel'
  }
]

// Statistiken
const stats = [
  { number: '25+', label: 'Jahre Erfahrung' },
  { number: '2000+', label: 'Gäste Kapazität' },
  { number: '150+', label: 'Events pro Jahr' },
  { number: '4', label: 'Einzigartige Räume' }
]

// Testimonials
const testimonials = [
  {
    id: 1,
    category: 'Brunch',
    categoryIcon: Coffee,
    name: 'Kathy Rik',
    text: 'Wir waren zum Brunchen am 2. Weihnachtstag dort. Das Ambiente ist sehr schön und gemütlich. Es wirkt rustikal, jedoch stylisch. Der Service ist sehr freundlich und aufmerksam. Das Essen ist köstlich und es wurde immer frisch nachgelegt, sodass man in Ruhe genießen konnte. Das Preis-Leistungsverhältnis ist absolut stimmig. Wir haben ein leckeres und gemütliches Weihnachts-Familien-Treffen dort verbracht und sagen Dankeschön.',
    rating: 5
  },
  {
    id: 2,
    category: 'Firmenfeier',
    categoryIcon: Briefcase,
    name: 'Heinemann DUS',
    text: 'Unsere Firmenfeier hier war ein voller Erfolg – ein Erlebnis, das wir so schnell nicht vergessen werden! Das Essen war köstlich und wurde mit viel Liebe zum Detail zubereitet. Das Team war ausgesprochen herzlich und hat uns jeden Wunsch von den Augen abgelesen. Man fühlt sich direkt wie zu Hause, was in dieser Branche nicht selbstverständlich ist. Die Location selbst ist einladend, rustikal; man merkt, dass viel Wert auf Atmosphäre gelegt wurde. Wir können diesen Ort wärmstens empfehlen!',
    rating: 5
  },
  {
    id: 3,
    category: 'Hochzeit',
    categoryIcon: Heart,
    name: 'Frauke Herbst',
    text: 'Großartige Hochzeit! Wir haben unsere Hochzeit hier gefeiert und alles war wirklich perfekt! Das Essen war super lecker und sah toll aus. Es wurde immer zügig nachgelegt. Selbst das Rindfleisch ging trotz vieler Rindfleischliebhaber aus Brasilien nicht zu Neige! Der Service war super freundlich und schnell. Unsere Gäste waren begeistert. Herr Reese und seine Frau sind auf all unsre Wünsche eingegangen und haben uns mit hilfreichen Tipps unterstützt. Auch die Preiskalkulation stimmte am Ende mit der Anfangskalkulation überein- wichtig für eine gute Planung. Wir würden immer wieder hier feiern und Freunde die unsere Gäste waren und demnächst heiraten werden dies auch tun. Wirklich top!',
    rating: 5
  },
  {
    id: 4,
    category: 'Firmenfeier',
    categoryIcon: Briefcase,
    name: 'Arnica Reiners',
    text: 'Wir feierten dort unser Betriebsfest von Helios. Knapp 1200 Menschen und alles war super organisiert 💯',
    rating: 5
  },
  {
    id: 5,
    category: 'Private Feier',
    categoryIcon: PartyPopper,
    name: 'Bernhard Roth',
    text: 'Ich habe mehrere Familienfeiern in gebuchten Räumen gehabt, kleine Abstecher zum Essen zwischendurch und mehrmals den Sonntagsbrunch genossen und bin rundum mit der Freundlichkeit und der Qualität und dem Preis/Leistungsverhältnis zufrieden. Wir kommen gerne wieder.',
    rating: 5
  },
  {
    id: 6,
    category: 'Party',
    categoryIcon: PartyPopper,
    name: 'D. Küppers',
    text: 'Nach ein paar Jahren Pause wieder "in der Hückels" Halloween feiern gewesen und es war richtig gut. Zwei Floors für alle was da gewesen! Publikum (jung und jungalt 😉) total nett! DJs sehr gut! Personal sehr freundlich! Verhungern kann man auch nicht. Es wurde vor Ort Pizza gebacken. Ich komme definitiv wieder!',
    rating: 5
  },
  {
    id: 7,
    category: 'Brunch',
    categoryIcon: Coffee,
    name: 'Astrid M.',
    text: 'Wir waren heute am 2. Weihnachtstag in HückelsMay. Das Ambiente sehr schön weihnachtlich geschmückt, das Essen war sehr lecker, Service schnell und gut organisiert. Wir hatten einen rundum schönen Brunch und haben uns sehr wohl gefühlt.',
    rating: 5
  },
  {
    id: 8,
    category: 'Firmenfeier',
    categoryIcon: Briefcase,
    name: 'Sven Wiszniewski',
    text: 'Absolut traumhafte Location für Firmenfeiern. Im Innenhof kommt man gemütlich und gesellig zusammen und innen fühlt man sich wohl. Perfekte Mischung aus Hof-, Land und Partyfeeling mit tollem Service und bester Ausstattung. Komme gerne immer wieder und freue mich auf das nächste Mal!',
    rating: 5
  },
  {
    id: 9,
    category: 'Private Feier',
    categoryIcon: Heart,
    name: 'May',
    text: 'Ich war letztens mit meinem Mann hier. Der Service und das Essen wirklich ausgezeichnet! Sehr romantisch, wir kommen auf jeden Fall wieder, vielen Dank!',
    rating: 5
  },
  {
    id: 10,
    category: 'Party',
    categoryIcon: PartyPopper,
    name: 'Tom Z.',
    text: 'Super freundliches Personal, wunderschöne Location und grandiose Partys!',
    rating: 5
  }
]

export default function Home() {
  const heroRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([])
  const [loadingEvents, setLoadingEvents] = useState(true)
  const [featuredBrunch, setFeaturedBrunch] = useState<Brunch | null>(null)
  const [nextBrunches, setNextBrunches] = useState<Brunch[]>([])
  const [loadingBrunch, setLoadingBrunch] = useState(true)
  const [currentTestimonial, setCurrentTestimonial] = useState(0)
  
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  // Events aus der Datenbank laden
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoadingEvents(true)
        const events = await getEvents()
        
        // Nur kommende Events (status = 'Aktiv' oder 'Geplant')
        const activeEvents = events.filter(
          (event: Event) => event.status === 'Aktiv' || event.status === 'Geplant'
        )
        
        // Sortiere nach Datum (neueste zuerst - aufsteigend nach Datum)
        const sortedEvents = activeEvents.sort((a: Event, b: Event) => {
          return new Date(a.date).getTime() - new Date(b.date).getTime()
        })
        
        // Die ersten 4 Events anzeigen
        const displayEvents = sortedEvents.slice(0, 4).map(transformEventForDisplay)
        
        setUpcomingEvents(displayEvents)
      } catch (error) {
        console.error('Fehler beim Laden der Events:', error)
        setUpcomingEvents([])
      } finally {
        setLoadingEvents(false)
      }
    }
    
    loadEvents()
  }, [])

  // Nächste Brunches aus der Datenbank laden
  useEffect(() => {
    const loadNextBrunches = async () => {
      try {
        setLoadingBrunch(true)
        const brunches = await brunchService.getNextBrunches()
        setNextBrunches(brunches)
        // Setze den ersten Brunch als featured für Kompatibilität
        setFeaturedBrunch(brunches.length > 0 ? brunches[0] : null)
      } catch (error) {
        console.error('Fehler beim Laden der nächsten Brunches:', error)
        setNextBrunches([])
        setFeaturedBrunch(null)
      } finally {
        setLoadingBrunch(false)
      }
    }
    
    loadNextBrunches()
  }, [])

  // Video-Lazy-Loading mit Intersection Observer
  useEffect(() => {
    const video = videoRef.current
    const hero = heroRef.current
    if (!video || !hero) return

    // Intersection Observer: Video erst laden, wenn Hero-Section sichtbar ist
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Video-Quelle setzen und laden
            if (!video.src) {
              video.src = '/hueckelsherovideo_komp.mp4'
              video.load()
            }
            
            const playVideo = async () => {
              try {
                await video.play()
                setIsVideoLoaded(true)
              } catch (error) {
                console.error('Video play failed:', error)
              }
            }

            video.addEventListener('loadeddata', playVideo, { once: true })
            observer.unobserve(hero)
          }
        })
      },
      { threshold: 0.1 }
    )

    observer.observe(hero)
    return () => {
      observer.disconnect()
      video.pause()
    }
  }, [])


  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => {
      // Wenn wir am Ende sind, gehe zurück zum Anfang
      if (prev >= testimonials.length - 1) {
        return 0
      }
      return prev + 1
    })
  }

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => {
      // Wenn wir am Anfang sind, gehe zum Ende
      if (prev <= 0) {
        return testimonials.length - 1
      }
      return prev - 1
    })
  }

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION - Cinematic Fullscreen
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-screen w-full overflow-hidden">
        {/* Video Background */}
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          {/* Poster-Bild als Fallback während Video lädt */}
          {!isVideoLoaded && (
            <img
              src="/logo_hero.png"
              alt="Hückels May"
              className="absolute inset-0 w-full h-full object-cover opacity-20"
              style={{ filter: 'brightness(0.4)' }}
            />
          )}
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.4)', opacity: isVideoLoaded ? 1 : 0, transition: 'opacity 0.5s ease-in' }}
          />
        </motion.div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        
        {/* Hero Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6"
        >
          {/* Logo */}
          <motion.img 
            src="/Logo_HM.png" 
            alt="Hückels May" 
            className="h-20 sm:h-24 md:h-32 lg:h-40 mb-6 md:mb-8 opacity-90"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3 }}
          />
          
          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="text-white/60 text-xs sm:text-sm md:text-base uppercase tracking-[0.4em] mb-4 md:mb-6"
          >
            Eventlocation am Niederrhein
          </motion.p>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.8 }}
            className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-8xl text-white font-light tracking-tight leading-[0.9] mb-8 md:mb-12"
          >
            Wo Momente<br />
            <span className="italic text-white/80">unvergesslich</span> werden
          </motion.h1>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 sm:px-8 md:px-10 py-3 md:py-4 bg-white text-black text-xs uppercase tracking-[0.3em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500"
              >
                Event anfragen
              </motion.button>
            </Link>
            <Link to="/galerie">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 sm:px-8 md:px-10 py-3 md:py-4 border border-white/30 text-white text-xs uppercase tracking-[0.3em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-2 md:gap-3"
              >
                <Play className="w-4 h-4" />
                Galerie
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: isVideoLoaded ? 1 : 0 }}
          transition={{ delay: 2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2"
          >
            <motion.div className="w-1 h-2 bg-white/60 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          INTRO SECTION - Elegant Statement
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-40 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-8"
          >
            Seit über 25 Jahren
          </motion.p>
          
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-serif text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-white font-light leading-tight mb-8 md:mb-12"
          >
            Wir verwandeln historische Räume in die 
            <span className="text-white/40"> Kulisse Ihrer besonderen Momente.</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to="/geschichte" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
              <span className="text-sm uppercase tracking-[0.2em]">Unsere Geschichte</span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          STATS BAR - Impressive Numbers
      ════════════════════════════════════════════════════════════ */}
      <section className="border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="py-12 md:py-16 px-6 text-center border-r border-white/5 last:border-r-0"
              >
                <p className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light mb-2">{stat.number}</p>
                <p className="text-white/40 text-xs uppercase tracking-[0.2em]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          EVENT CATEGORIES - Interactive Showcase
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-32 md:py-48">
        <div className="max-w-[1600px] mx-auto px-6">
          
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 md:mb-24">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
              >
                Ihr Event, Ihre Wahl
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light"
              >
                Wofür planen Sie<br />
                <span className="italic text-white/60">Ihr Event?</span>
              </motion.h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-8 md:mt-0"
            >
              <Link to="/veranstaltungen" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
                <span className="text-sm uppercase tracking-[0.2em]">Alle Events</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </motion.div>
          </div>

          {/* 3-Spalten Kachel-Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {eventCategories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <Link 
                  to={category.link}
                  className="group block h-full"
                >
                  <div className="relative h-full rounded-2xl overflow-hidden border border-white/5 hover:border-white/15 transition-all duration-500 bg-[#111]">
                    {/* Bild */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={category.image}
                        alt={category.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/40 to-transparent" />
                      
                      {/* Capacity Badge */}
                      <div className="absolute bottom-4 left-4 px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full border border-white/10">
                        <div className="flex items-center gap-2">
                          <Users className="w-3.5 h-3.5 text-white/60" />
                          <span className="text-white text-xs">{category.capacity}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white/40 text-[10px] uppercase tracking-[0.3em] mb-1.5">{category.subtitle}</p>
                          <h3 className="font-serif text-xl md:text-2xl text-white font-light">{category.title}</h3>
                        </div>
                        <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center transition-all duration-500 group-hover:bg-brand-800 group-hover:border-brand-800">
                          <ArrowUpRight className="w-4 h-4 text-white/40 transition-colors group-hover:text-white" />
                        </div>
                      </div>
                      <p className="text-white/40 text-sm leading-relaxed">{category.description}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          KOMMENDE EVENTS - Party Events aus /partys
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-32 md:py-48 overflow-hidden">
        {/* Section Header mit Padding */}
        <div className="max-w-[1600px] mx-auto px-6 mb-16 md:mb-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
              >
                Nicht verpassen
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light"
              >
                Kommende Events
              </motion.h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-8 md:mt-0"
            >
              <Link to="/partys" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
                <span className="text-sm uppercase tracking-[0.2em]">Alle Events</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Events Grid - Konsistentes Design */}
        <div className="max-w-[1600px] mx-auto px-6">
          {loadingEvents ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-2xl overflow-hidden bg-[#151515] border border-white/5 animate-pulse">
                  <div className="relative aspect-[4/3] bg-gray-800" />
                  <div className="p-4 md:p-5">
                    <div className="h-4 bg-gray-700 rounded mb-2 w-2/3" />
                    <div className="h-3 bg-gray-700 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link to={`/partys/${event.slug}`} className="group block h-full">
                    <div className="relative h-full rounded-2xl overflow-hidden border border-white/5 hover:border-white/10 transition-all duration-300 bg-[#151515]">
                      {/* Vorschaubild */}
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img 
                          src={getEventImageUrl(event.imageUrl)} 
                          alt={event.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          onError={(e) => {
                            if ((e.currentTarget as HTMLImageElement).dataset.flyerFallback === '1') return
                            ;(e.currentTarget as HTMLImageElement).dataset.flyerFallback = '1'
                            e.currentTarget.src = EVENT_IMAGE_FALLBACK
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/40 to-transparent" />
                        
                        {/* Kategorie Badge */}
                        {event.category && (
                          <div className="absolute bottom-4 left-4 px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full border border-white/10">
                            <span className="text-white text-xs font-medium">{event.category}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-4 md:p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-serif text-lg md:text-xl text-white font-light mb-2 line-clamp-2 group-hover:text-brand-400 transition-colors">
                              {event.title}
                            </h3>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-brand-800 transition-colors duration-300 flex-shrink-0 ml-3">
                            <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
                          </div>
                        </div>
                        
                        {/* Datum & Zeit */}
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-white/60 text-xs">
                            <Calendar className="w-3.5 h-3.5 text-brand-400" />
                            <span>{event.date}</span>
                          </div>
                          {event.time && (
                            <div className="flex items-center gap-2 text-white/60 text-xs">
                              <Clock className="w-3.5 h-3.5 text-brand-400" />
                              <span>{event.time}</span>
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-2 text-white/50 text-xs">
                              <MapPin className="w-3 h-3 text-brand-400" />
                              <span className="truncate">{event.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/40 text-sm">Aktuell keine kommenden Events</p>
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          NÄCHSTER BRUNCH - Kompakte Anzeige
      ════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center">
            
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4">Kulinarisches Erlebnis</p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-light mb-6">
                Nächster Brunch
              </h2>
              <p className="text-white/50 text-lg leading-relaxed mb-8 max-w-lg">
                Genießen Sie unser exquisites Brunch-Buffet in einzigartiger Atmosphäre. 
                Ein kulinarisches Erlebnis für die ganze Familie.
              </p>
              
              {/* Next Brunch Info */}
              {loadingBrunch ? (
                <div className="bg-[#151515] rounded-2xl p-6 border border-white/5 mb-8">
                  <div className="flex items-start gap-5">
                    <div className="w-16 h-16 rounded-xl bg-brand-800/20 flex items-center justify-center flex-shrink-0 animate-pulse">
                      <div className="w-8 h-8 bg-white/10 rounded" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-white/10 rounded w-32 animate-pulse" />
                      <div className="h-6 bg-white/10 rounded w-48 animate-pulse" />
                      <div className="h-4 bg-white/10 rounded w-40 animate-pulse" />
                    </div>
                  </div>
                </div>
              ) : nextBrunches.length > 0 ? (
                <div className="space-y-4 mb-8">
                  {nextBrunches.map((brunch, index) => {
                    const Icon = iconMap[brunch.icon_name] || Coffee
                    return (
                      <div key={brunch.id || index} className="bg-[#151515] rounded-2xl p-6 border border-white/5">
                        <div className="flex items-start gap-5">
                          <div className="w-16 h-16 rounded-xl bg-brand-800/20 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-8 h-8 text-brand-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-white/40 text-xs uppercase tracking-wider mb-1">
                              {brunch.event_date ? (
                                (() => {
                                  try {
                                    const date = new Date(brunch.event_date)
                                    if (isNaN(date.getTime())) {
                                      return 'Datum folgt'
                                    }
                                    return date.toLocaleDateString('de-DE', { 
                                      day: 'numeric', 
                                      month: 'long', 
                                      year: 'numeric' 
                                    })
                                  } catch (e) {
                                    return 'Datum folgt'
                                  }
                                })()
                              ) : 'Datum folgt'}
                            </p>
                            <h3 className="font-serif text-2xl text-white font-light mb-2">{brunch.title}</h3>
                            <p className="text-white/50 text-sm">
                              {brunch.event_time || 'Uhrzeit folgt'}
                              {brunch.subtitle && ` · ${brunch.subtitle}`}
                              {brunch.price && ` · ${brunch.price}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Link to="/brunch">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-6 md:px-8 py-3 md:py-4 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500"
                  >
                    Jetzt reservieren
                  </motion.button>
                </Link>
                <Link to="/brunch">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    className="px-6 md:px-8 py-3 md:py-4 border border-white/20 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/5 transition-all duration-500"
                  >
                    Alle Termine
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            {/* Right: Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
                <img 
                  src={nextBrunches.length > 0 
                    ? (nextBrunches[0].preview_image_url || nextBrunches[0].image_url || nextBrunches[0].flyer_image_url || "/assets/brunch/brunch_vorschau_fallback.jpg")
                    : "/assets/brunch/brunch_vorschau_fallback.jpg"
                  } 
                  alt={nextBrunches.length > 0 ? nextBrunches[0].title : "Brunch Ambiente"}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    if (e.currentTarget.src !== '/assets/brunch/brunch_vorschau_fallback.jpg') {
                      e.currentTarget.src = '/assets/brunch/brunch_vorschau_fallback.jpg'
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          TESTIMONIALS SLIDER - Horizontal Carousel
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-20 md:py-28 overflow-hidden border-t border-white/5">
        {/* Ambient Background */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/10 via-transparent to-transparent" />
        
        <div className="max-w-[1600px] mx-auto px-6 relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/10 border border-brand-800/20 rounded-full mb-4 backdrop-blur-sm">
              <Star className="w-3.5 h-3.5 text-brand-400" />
              <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-semibold">Kundenstimmen</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light mb-3 tracking-tight">
              Was unsere Gäste <span className="italic text-brand-400">erleben</span>
            </h2>
            <p className="text-white/50 text-base max-w-xl mx-auto">
              Echte Erfahrungen von zufriedenen Gästen
            </p>
          </motion.div>

          {/* Horizontal Scrollable Container */}
          <div className="relative">
            {/* Navigation Buttons */}
            <motion.button
              onClick={prevTestimonial}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 hover:border-brand-800/50 flex items-center justify-center transition-all duration-300 group shadow-lg hover:shadow-brand-800/20 -ml-2 md:-ml-4"
              aria-label="Vorherige Bewertungen"
            >
              <ChevronLeft className="w-5 h-5 text-white/60 group-hover:text-brand-400 transition-colors" />
            </motion.button>

            <motion.button
              onClick={nextTestimonial}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 hover:border-brand-800/50 flex items-center justify-center transition-all duration-300 group shadow-lg hover:shadow-brand-800/20 -mr-2 md:-mr-4"
              aria-label="Nächste Bewertungen"
            >
              <ChevronRight className="w-5 h-5 text-white/60 group-hover:text-brand-400 transition-colors" />
            </motion.button>

            {/* Testimonials Horizontal Scroll */}
            <div className="overflow-hidden px-4 md:px-8">
              <motion.div 
                className="flex gap-3 sm:gap-4 md:gap-5"
                animate={{ 
                  x: `calc(-${currentTestimonial} * (260px + 1rem))`,
                }}
                transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
              >
                {testimonials.map((testimonial, index) => {
                  const IconComponent = testimonial.categoryIcon
                  return (
                    <motion.div
                      key={testimonial.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="w-[280px] sm:w-[300px] md:w-[320px] flex-shrink-0"
                    >
                      <div className="relative group h-full">
                        {/* Card */}
                        <div className="relative bg-gradient-to-br from-zinc-900/60 via-zinc-900/70 to-black/80 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden shadow-lg hover:border-brand-800/30 transition-all duration-300 h-full flex flex-col">
                          {/* Top Accent */}
                          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-brand-400/50 to-transparent" />
                          
                          {/* Content */}
                          <div className="p-4 flex flex-col flex-1">
                            {/* Header */}
                            <div className="flex flex-col gap-2 mb-3">
                              {/* Category */}
                              <div className="flex items-center gap-2 px-2.5 py-1 bg-gradient-to-r from-brand-800/20 to-brand-900/10 border border-brand-700/20 rounded-lg w-fit">
                                <IconComponent className="w-3 h-3 text-brand-400" />
                                <span className="text-brand-300 text-xs font-medium">
                                  {testimonial.category}
                                </span>
                              </div>
                              
                              {/* Stars */}
                              <div className="flex items-center gap-0.5">
                                {[...Array(testimonial.rating)].map((_, i) => (
                                  <Star key={i} className="w-3 h-3 text-brand-400 fill-brand-400" />
                                ))}
                              </div>
                            </div>

                            {/* Quote */}
                            <blockquote className="relative z-10 text-white/90 text-xs leading-relaxed mb-3 flex-1">
                              <Quote className="w-5 h-5 text-brand-400/20 absolute -top-1 -left-1" />
                              <span className="relative">{testimonial.text}</span>
                            </blockquote>

                            {/* Author */}
                            <div className="flex items-center gap-2.5 pt-3 border-t border-white/5">
                              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-brand-800/30 to-brand-900/20 border border-brand-700/20 flex items-center justify-center">
                                <span className="text-brand-400 text-xs font-serif font-semibold">
                                  {testimonial.name.charAt(0)}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-medium text-xs truncate">
                                  {testimonial.name}
                                </p>
                                <p className="text-white/40 text-[10px]">Verifizierter Gast</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </motion.div>
            </div>

            {/* Dots Indicator */}
            <div className="flex items-center justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentTestimonial
                      ? 'w-8 h-2 bg-brand-400'
                      : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                  }`}
                  aria-label={`Bewertung ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Trust Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center mt-10"
          >
            <div className="inline-flex items-center gap-2 text-white/40 text-sm">
              <Award className="w-4 h-4 text-brand-400" />
              <span>Über 4500 erfolgreiche Events seit 1998</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          RÄUME PREVIEW - Visual Teaser
      ════════════════════════════════════════════════════════════ */}
      <section className="py-32 md:py-48 border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
              >
                Die Location
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-serif text-4xl md:text-6xl text-white font-light"
              >
                Vier einzigartige Räume
              </motion.h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-6 md:mt-0"
            >
              <Link to="/raeume" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
                <span className="text-sm uppercase tracking-[0.2em]">Alle Räume</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </motion.div>
          </div>
          
          {/* Rooms Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { name: 'Festscheune', slug: 'fest-scheune', image: '/assets/raume/fest.jpg', capacity: '400' },
              { name: 'Weinstube', slug: 'weinstube', image: '/assets/raume/wein.jpg', capacity: '30' },
              { name: 'Landhaus', slug: 'landhaus', image: '/assets/raume/land.jpg', capacity: '100' },
              { name: 'Innenhof', slug: 'innenhof', image: '/assets/home_pics/HK_277.JPG', capacity: '700' }
            ].map((room, index) => (
              <motion.div
                key={room.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={`/raeume/${room.slug}`} className="group block">
                  <div className="rounded-2xl overflow-hidden bg-[#151515] border border-white/5 hover:border-white/10 transition-all duration-300">
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img 
                        src={room.image} 
                        alt={room.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                    {/* Text unterhalb des Bildes */}
                    <div className="p-4 md:p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-white text-lg md:text-xl font-serif font-light mb-1">{room.name}</h3>
                          <p className="text-white/40 text-xs">Bis {room.capacity} Gäste</p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-brand-800 transition-colors duration-300">
                          <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION - Final Push
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Bereit?</p>
            
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light mb-8 leading-tight">
              Lassen Sie uns Ihr<br />
              <span className="italic text-white/60">nächstes Event</span> planen
            </h2>
            
            <p className="text-white/40 text-lg md:text-xl mb-12 max-w-2xl mx-auto">
              Erzählen Sie uns von Ihrer Vision. Wir freuen uns darauf, gemeinsam mit Ihnen etwas Besonderes zu erschaffen.
            </p>

            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 sm:px-10 md:px-14 py-4 md:py-5 bg-white text-black text-sm uppercase tracking-[0.3em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500"
              >
                Kontakt aufnehmen
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
